import React, { useCallback, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import { ModalCloseButton } from './icons/AdminUiIcons';
import { ShopMobileDrillPanel } from './ShopNavMenu';
import './mobile-nav-drawer.css';

function IconChevronRight({ className }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

function IconChevronLeft({ className }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

/**
 * App-style full-screen mobile menu: sticky search + Sign in, Shop as a
 * second panel (drill-down) instead of an accordion that pushes CTAs away.
 */
export default function MobileNavDrawer({
  open,
  onClose,
  store,
  onNavLinkClick,
  showCustomJewelryNav,
  customJewelryNavLabel,
  searchSlot,
  accountSlot,
  shopSectionActive,
}) {
  const titleId = useId();
  const [panel, setPanel] = useState('root');

  useEffect(() => {
    if (!open) setPanel('root');
  }, [open]);

  useEffect(() => {
    if (!open) {
      document.body.classList.remove('mobile-nav-open');
      return undefined;
    }
    document.body.classList.add('mobile-nav-open');
    const scrollY = window.scrollY;
    const prev = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.classList.remove('mobile-nav-open');
      document.body.style.overflow = prev.overflow;
      document.body.style.position = prev.position;
      document.body.style.top = prev.top;
      document.body.style.width = prev.width;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (panel === 'shop') setPanel('root');
        else onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, panel]);

  const goShop = useCallback(() => setPanel('shop'), []);
  const goRoot = useCallback(() => setPanel('root'), []);

  const link = (to, opts) => {
    const handler = onNavLinkClick?.(to, opts);
    if (!handler) return undefined;
    return (e) => {
      handler(e);
      onClose();
    };
  };

  if (!open) return null;

  return createPortal(
    <div className="mobile-nav-drawer" role="presentation">
      <button
        type="button"
        className="mobile-nav-drawer__backdrop"
        aria-label="Close menu"
        onClick={onClose}
      />
      <div
        className="mobile-nav-drawer__sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        id="mobile-nav-drawer"
      >
        <header className="mobile-nav-drawer__header">
          {panel === 'shop' ? (
            <>
              <button
                type="button"
                className="mobile-nav-drawer__back"
                onClick={goRoot}
                aria-label="Back to menu"
              >
                <IconChevronLeft />
                <span>Menu</span>
              </button>
              <h2 id={titleId} className="mobile-nav-drawer__title mobile-nav-drawer__title--center">
                {store.nav_shop}
              </h2>
            </>
          ) : (
            <h2 id={titleId} className="mobile-nav-drawer__title">
              Menu
            </h2>
          )}
          <ModalCloseButton onClick={onClose} className="mobile-nav-drawer__close" />
        </header>

        <div className="mobile-nav-drawer__search">{searchSlot}</div>

        <div className="mobile-nav-drawer__panels" data-panel={panel}>
          <div
            className={`mobile-nav-drawer__track${panel === 'shop' ? ' mobile-nav-drawer__track--shop' : ''}`}
          >
            <nav className="mobile-nav-drawer__panel" aria-label="Primary" aria-hidden={panel !== 'root'}>
              <ul className="mobile-nav-drawer__list">
                <li>
                  <NavLink
                    className={({ isActive }) =>
                      `mobile-nav-drawer__link${isActive ? ' is-active' : ''}`
                    }
                    to="/"
                    end
                    onClick={link('/', { end: true })}
                  >
                    {store.nav_home}
                  </NavLink>
                </li>
                <li>
                  <button
                    type="button"
                    className={`mobile-nav-drawer__link mobile-nav-drawer__link--push${
                      shopSectionActive ? ' is-active' : ''
                    }`}
                    onClick={goShop}
                  >
                    <span className="mobile-nav-drawer__link-copy">
                      <span className="mobile-nav-drawer__link-label">{store.nav_shop}</span>
                      <span className="mobile-nav-drawer__link-hint">{store.shop_nav_mobile_browse}</span>
                    </span>
                    <IconChevronRight className="mobile-nav-drawer__chev" />
                  </button>
                </li>
                <li>
                  <NavLink
                    className={({ isActive }) =>
                      `mobile-nav-drawer__link${isActive ? ' is-active' : ''}`
                    }
                    to="/about"
                    onClick={link('/about')}
                  >
                    {store.nav_about}
                  </NavLink>
                </li>
                {showCustomJewelryNav ? (
                  <li>
                    <NavLink
                      className={({ isActive }) =>
                        `mobile-nav-drawer__link${isActive ? ' is-active' : ''}`
                      }
                      to="/custom-jewelry"
                      onClick={link('/custom-jewelry')}
                    >
                      {customJewelryNavLabel}
                    </NavLink>
                  </li>
                ) : null}
                <li>
                  <NavLink
                    className={({ isActive }) =>
                      `mobile-nav-drawer__link${isActive ? ' is-active' : ''}`
                    }
                    to="/exhibitions"
                    onClick={link('/exhibitions')}
                  >
                    {store.nav_exhibitions}
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    className={({ isActive }) =>
                      `mobile-nav-drawer__link${isActive ? ' is-active' : ''}`
                    }
                    to="/contact"
                    onClick={link('/contact')}
                  >
                    {store.nav_contact}
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    className={({ isActive }) =>
                      `mobile-nav-drawer__link${isActive ? ' is-active' : ''}`
                    }
                    to="/track-order"
                    onClick={link('/track-order')}
                  >
                    {store.nav_track_order}
                  </NavLink>
                </li>
              </ul>
            </nav>

            <div
              className="mobile-nav-drawer__panel mobile-nav-drawer__panel--shop"
              aria-hidden={panel !== 'shop'}
            >
              <ShopMobileDrillPanel onNavLinkClick={onNavLinkClick} store={store} onNavigate={onClose} />
            </div>
          </div>
        </div>

        <footer className="mobile-nav-drawer__footer">{accountSlot}</footer>
      </div>
    </div>,
    document.body
  );
}
